/**
 * @module svg/icons
 *
 * A tiny inline-SVG icon factory. Every glyph is authored on a 24x24 viewBox
 * and inherits `currentColor`, so icons can be recoloured purely from CSS.
 *
 * Icons are decorative by default (`aria-hidden="true"`); the accessible name
 * always comes from the surrounding text or an explicit label.
 */

/**
 * Raw path/shape data for each glyph. Keeping the geometry as data (rather than
 * as markup scattered through components) keeps the components readable.
 *
 * @type {Record<string, string>}
 */
const GLYPH_DATA = {
  // --- Phase-agnostic UI -------------------------------------------------
  arrowRight: '<path d="M4 12h14m0 0-5.5-5.5M18 12l-5.5 5.5"/>',
  arrowDown: '<path d="M12 4v14m0 0 5.5-5.5M12 18l-5.5-5.5"/>',
  chevronLeft: '<path d="M14.5 5.5 8 12l6.5 6.5"/>',
  chevronRight: '<path d="M9.5 5.5 16 12l-6.5 6.5"/>',
  close: '<path d="M6 6l12 12M18 6 6 18"/>',
  menu: '<path d="M4 7h16M4 12h16M4 17h16"/>',
  external: '<path d="M14 4h6v6M20 4l-8 8M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"/>',
  check: '<path d="M4 12.5 9.5 18 20 6.5"/>',
  spark: '<path d="M12 3l1.9 5.4L19 10l-5.1 1.6L12 17l-1.9-5.4L5 10l5.1-1.6L12 3Z"/>',
  play: '<path d="M8 5.5v13l11-6.5-11-6.5Z"/>',
  pause: '<path d="M9 5v14M15 5v14"/>',

  // --- Strategic pillars -------------------------------------------------
  layers:
    '<path d="M12 3 3 7.5 12 12l9-4.5L12 3Z"/><path d="m3 12 9 4.5L21 12"/><path d="m3 16.5 9 4.5 9-4.5"/>',
  sprint:
    '<path d="M3 17.5c3-6 6.5-9 10-9 2.6 0 4.6 1.4 5.6 3.6"/><path d="M14.5 12.1 21 12l-3.2-5.6"/><circle cx="4" cy="19" r="1.6"/>',
  compass:
    '<circle cx="12" cy="12" r="9"/><path d="m15.5 8.5-2.2 5-5 2.2 2.2-5 5-2.2Z"/>',

  // --- Workstreams -------------------------------------------------------
  gantt:
    '<path d="M4 6h9M4 12h14M4 18h6"/><path d="M4 3v18"/>',
  target:
    '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1" fill="currentColor"/>',
  server:
    '<rect x="3" y="4" width="18" height="7" rx="2"/><rect x="3" y="13" width="18" height="7" rx="2"/><path d="M7 7.5h.01M7 16.5h.01"/>',
  sliders:
    '<path d="M6 4v7m0 4v5M12 4v3m0 4v9M18 4v11m0 4v1"/><circle cx="6" cy="13" r="2"/><circle cx="12" cy="9" r="2"/><circle cx="18" cy="17" r="2"/>',
  plug: '<path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8ZM12 17v4"/>',
  database:
    '<ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6"/><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/>',
  book: '<path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2V5Z"/><path d="M8 3v18M19 16h-9"/>',
  people:
    '<circle cx="9" cy="8" r="3.2"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 5.5a3.2 3.2 0 0 1 0 6.4M17 14.4a6 6 0 0 1 4 5.6"/>',
  rocket:
    '<path d="M14.5 3.5C18 5 20 8 20 12c0 0-3 1-6-2-3-3-2-6-2-6s1.5-.9 2.5-.5Z"/><path d="M13 11 6 18M9 15l-3 .5L6.5 12M13 11l3 3"/>',
  pulse: '<path d="M3 12h4l2.5-6 5 12L17 12h4"/>',

  // --- Search, filters and section chrome --------------------------------
  search: '<circle cx="11" cy="11" r="6.5"/><path d="m16 16 4.5 4.5"/>',
  filter: '<path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z"/>',
  grid: '<rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/>',
  list: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  quote:
    '<path d="M9 6.5C6.5 8 5 10.3 5 13.2c0 2.4 1.3 4.3 3.4 4.3 1.8 0 3.1-1.3 3.1-3.1 0-1.7-1.2-2.9-2.8-2.9-.4 0-.7 0-1 .2.3-1.3 1.3-2.5 2.7-3.4L9 6.5Zm8.6 0c-2.5 1.5-4 3.8-4 6.7 0 2.4 1.3 4.3 3.4 4.3 1.8 0 3.1-1.3 3.1-3.1 0-1.7-1.2-2.9-2.8-2.9-.4 0-.7 0-1 .2.3-1.3 1.3-2.5 2.7-3.4l-1.4-1.8Z"/>',

  // --- AI, cloud and governance ------------------------------------------
  cloud: '<path d="M7 18h9.5a3.5 3.5 0 0 0 .4-7 5 5 0 0 0-9.6-1.2A3.9 3.9 0 0 0 7 18Z"/>',
  chip: '<rect x="7" y="7" width="10" height="10" rx="2"/><path d="M10 3.5v3M14 3.5v3M10 17.5v3M14 17.5v3M3.5 10h3M3.5 14h3M17.5 10h3M17.5 14h3"/>',
  shield: '<path d="M12 3 5 6v6c0 4 3 6.9 7 8 4-1.1 7-4 7-8V6l-7-3Z"/><path d="m9 12 2 2 4-4"/>',
  scale:
    '<path d="M12 4v16M7 20h10M4 9h16M6 9l-2.5 5.5a3 3 0 0 0 5 0L6 9Zm12 0-2.5 5.5a3 3 0 0 0 5 0L18 9Z"/><circle cx="12" cy="4" r="1.4"/>',
  lock: '<rect x="4.5" y="10" width="15" height="10" rx="2.5"/><path d="M8 10V7.5a4 4 0 0 1 8 0V10"/>',
  graph:
    '<circle cx="6" cy="18" r="2.2"/><circle cx="18" cy="6" r="2.2"/><circle cx="18" cy="18" r="2.2"/><path d="M7.9 16.5 16 7.6M8.2 18h7.6"/>',
  orbit: '<circle cx="12" cy="12" r="3"/><ellipse cx="12" cy="12" rx="9.5" ry="4.2" transform="rotate(-26 12 12)"/><circle cx="20.2" cy="8.6" r="1.4" fill="currentColor" stroke="none"/>',
  layers3:
    '<path d="M12 3 3 7.2 12 11.4l9-4.2L12 3Z"/><path d="m3 11.4 9 4.2 9-4.2"/><path d="m3 15.6 9 4.2 9-4.2"/>',
  arrowUpRight: '<path d="M7 17 17 7M9 7h8v8"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  minus: '<path d="M5 12h14"/>',
  compassRose:
    '<circle cx="12" cy="12" r="9"/><path d="m15.5 8.5-2.2 5-5 2.2 2.2-5 5-2.2Z"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2"/>',
};

/**
 * Build an SVG icon string.
 *
 * @param {string} name  Key from {@link GLYPH_DATA}.
 * @param {Object} [options]
 * @param {number} [options.size=24]         Rendered width/height in px.
 * @param {string} [options.className=""]    Extra class names.
 * @param {string} [options.label]           When given, the icon becomes a
 *   labelled `img` role instead of being hidden from assistive tech.
 * @param {number} [options.strokeWidth=1.5] Stroke width for outline glyphs.
 * @returns {string} SVG markup.
 */
export function icon(name, options = {}) {
  const { size = 24, className = "", label, strokeWidth = 1.5 } = options;
  const data = GLYPH_DATA[name];

  if (!data) {
    // Defensive: an unknown glyph should never crash a render pass.
    if (import.meta.env?.DEV) {
      console.warn(`[svg/icons] Unknown glyph "${name}"`);
    }
    return "";
  }

  const a11y = label
    ? `role="img" aria-label="${escapeAttribute(label)}"`
    : 'aria-hidden="true" focusable="false"';

  return [
    `<svg class="icon ${className}" width="${size}" height="${size}"`,
    ` viewBox="0 0 24 24" fill="none" stroke="currentColor"`,
    ` stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"`,
    ` data-icon="${name}" ${a11y}>`,
    data,
    "</svg>",
  ].join("");
}

/**
 * Escape a string for safe use inside a double-quoted HTML attribute.
 *
 * @param {string} value
 * @returns {string}
 */
export function escapeAttribute(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * Escape a string for safe use as HTML text content.
 *
 * @param {string} value
 * @returns {string}
 */
export function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

export { GLYPH_DATA };

/**
 * Fill every `[data-icon-slot="name"]` placeholder in the document with the
 * corresponding inline SVG.
 *
 * This keeps the HTML readable (`<span data-icon-slot="arrowRight"></span>`
 * instead of a wall of path data) while still guaranteeing the icon and its
 * accessible name live next to the label they belong to.
 *
 * @param {ParentNode} [root=document]
 * @returns {number} How many slots were filled.
 */
export function hydrateIconSlots(root = document) {
  const slots = Array.from(root.querySelectorAll("[data-icon-slot]"));
  let filled = 0;

  slots.forEach((slot) => {
    if (slot.querySelector("svg")) return; // already hydrated

    const name = slot.getAttribute("data-icon-slot");
    if (!name) return;

    const markup = icon(name, { size: 18 });
    if (!markup) return;

    slot.innerHTML = markup;
    filled += 1;
  });

  return filled;
}

